import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const revenueModels = [
    'subscription',
    'transactional'
]

const RevenueValueDriverMarketingForm = forwardRef(({ currency, outcome, outcomeList, editMarketingDriverId, editMarketingDriver, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        cac: "Customer Acquisition Costs (CAC) represent the total expenses incurred to acquire a new customer, including marketing, sales, and related overhead.",
        cacChange: `${positiveDriver ? "Decrease" : "Increase"} in Customer Acquisition Costs when using your product instead of the competitive alternative.`,
    }

    const handleRevenueModelChange = (event) => {
        formik.setFieldValue('revenue_model', event.target.value)
    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editMarketingDriverId) {
            setPositiveDriver(editMarketingDriver["positive_driver"])

            formik.setFieldValue("customer_acquisition_costs", editMarketingDriver["customer_acquisition_costs"])
            formik.setFieldValue("cac_decrease", editMarketingDriver["cac_decrease"])
            formik.setFieldValue("revenue_model", editMarketingDriver["revenue_model"])
            formik.setFieldValue("annual_contract_value", formatNumberThsndAndDec(editMarketingDriver["annual_contract_value"]))
            formik.setFieldValue("transaction_value", formatNumberThsndAndDec(editMarketingDriver["transaction_value"]))
            formik.setFieldValue("transaction_frequency", formatNumberThsndAndDec(editMarketingDriver["transaction_frequency"]))
            formik.setFieldValue("contribution_margin", editMarketingDriver["contribution_margin"])
            formik.setFieldValue("positive_driver", editMarketingDriver["positive_driver"])
            formik.setFieldValue("outcome", editMarketingDriver["outcome"])
            formik.setFieldValue("driver_name", editMarketingDriver["driver_name"])
            formik.setFieldValue("comment", editMarketingDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMarketingDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `CAC ${positiveDriver ? 'Decrease' : 'Increase'}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver])

    const initialValues = {
        id: null,
        revenue_type: 'conversion',
        outcome: '',
        driver_name: `CAC ${positiveDriver ? 'Decrease' : 'Increase'}`,
        comment: '',
        positive_driver: true,
        type: 'conversion',
        currency: currency,
        customer_acquisition_costs: 70,
        cac_decrease: 0,
        annual_contract_value: 0,
        transaction_value: 0,
        transaction_frequency: 0,
        contribution_margin: 0,
        revenue_model: revenueModels[0]
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        if (!isNumeric(values.customer_acquisition_costs)) {
            errors.customer_acquisition_costs = 'Must be numeric value';
        } else if (!isPositive(values.customer_acquisition_costs)) {
            errors.customer_acquisition_costs = 'Must be positive or zero';
        }

        if (!isNumeric(values.cac_decrease)) {
            errors.cac_decrease = 'Must be numeric value';
        } else if (!isPositive(values.cac_decrease)) {
            errors.cac_decrease = 'Must be positive or zero';
        } else if (values.cac_decrease > 100) {
            errors.cac_decrease = 'Not more than 100';
        }

        if (!isNumeric(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be numeric value';
        } else if (!isPositive(values.annual_contract_value)) {
            errors.annual_contract_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.contribution_margin)) {
            errors.contribution_margin = 'Must be numeric value';
        } else if (!isPositive(values.contribution_margin)) {
            errors.contribution_margin = 'Must be positive or zero';
        } else if (values.contribution_margin > 100) {
            errors.contribution_margin = 'Not more than 100';
        }

        if (!isNumeric(values.transaction_value)) {
            errors.transaction_value = 'Must be numeric value';
        } else if (!isPositive(values.transaction_value)) {
            errors.transaction_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be numeric value';
        } else if (!isPositive(values.transaction_frequency)) {
            errors.transaction_frequency = 'Must be positive or zero';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            setPositiveDriver(true)
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1300 }}>
            <Grid container columnSpacing={2} alignItems="center" rowSpacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            1. Conversion
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Customer Acquisition Costs (CAC){tooltip(tooltipText.cac)}
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ mt: 3 }}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                {`CAC ${positiveDriver ? 'Decrease' : 'Increase'}`}{tooltip(tooltipText.cacChange)}
                            </Typography>
                        </Grid>
                        <Grid item xs={10} />
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="customer_acquisition_costs"
                                startAdornment={currency}
                                value={formik.values.customer_acquisition_costs}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("customer_acquisition_costs", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "customer_acquisition_costs") && Boolean(getIn(formik.errors, "customer_acquisition_costs"))}
                                helperText={getIn(formik.touched, "customer_acquisition_costs") && getIn(formik.errors, "customer_acquisition_costs")}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="cac_decrease"
                                endAdornment="%"
                                value={formik.values.cac_decrease}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("cac_decrease", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "cac_decrease") && Boolean(getIn(formik.errors, "cac_decrease"))}
                                helperText={getIn(formik.touched, "cac_decrease") && getIn(formik.errors, "cac_decrease")}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                        </Grid>
                        <Grid item xs={8} />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            2. Profit per customer
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption">
                            Your Customers' revenue model{tooltip(generalTooltipText.revenueModel)}
                        </Typography>
                        <Box>
                            <Select
                                open={open}
                                onClose={handleClose}
                                onOpen={handleOpen}
                                value={formik.values.revenue_model}
                                onChange={handleRevenueModelChange}
                                size='small'
                            >
                                {revenueModels.map((model) => (
                                    <MenuItem key={model} value={model}>
                                        {firstCharToUpper(model)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {formik.values.revenue_model === 'subscription' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Annual Contract Value (ACV){tooltip(generalTooltipText.acv)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="annual_contract_value"
                                        startAdornment={currency}
                                        value={formik.values.annual_contract_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("annual_contract_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "annual_contract_value") && Boolean(getIn(formik.errors, "annual_contract_value"))}
                                        helperText={getIn(formik.touched, "annual_contract_value") && getIn(formik.errors, "annual_contract_value")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="contribution_margin"
                                        endAdornment="%"
                                        value={formik.values.contribution_margin}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("contribution_margin", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                        helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null}
                {formik.values.revenue_model === 'transactional' ?
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Grid container columnGap={2}>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Transaction value{tooltip(generalTooltipText.transactionValue)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Transaction frequency{tooltip(generalTooltipText.transactionFrequency)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="transaction_value"
                                        startAdornment={currency}
                                        value={formik.values.transaction_value}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_value", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_value") && Boolean(getIn(formik.errors, "transaction_value"))}
                                        helperText={getIn(formik.touched, "transaction_value") && getIn(formik.errors, "transaction_value")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="transaction_frequency"
                                        endAdornment={
                                            <Typography variant="caption">
                                                times/year
                                            </Typography>
                                        }
                                        value={formik.values.transaction_frequency}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("transaction_frequency", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "transaction_frequency") && Boolean(getIn(formik.errors, "transaction_frequency"))}
                                        helperText={getIn(formik.touched, "transaction_frequency") && getIn(formik.errors, "transaction_frequency")}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextFieldCustom
                                        name="contribution_margin"
                                        endAdornment="%"
                                        value={formik.values.contribution_margin}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            const formattedValue = formatNumberThsndAndDec(value)
                                            formik.setFieldValue("contribution_margin", formattedValue || '')
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                        helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                    : null}
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            3. Driver details
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                        </Typography>
                        <GreenRedCompactSwitch
                            name="positive_driver"
                            leftLabel="Negative"
                            rightLabel="Positive"
                            value={formik.values.positive_driver}
                            onChange={(event) => {
                                formik.handleChange(event)
                                setPositiveDriver(!positiveDriver)
                            }}
                        />

                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {!outcome &&
                    (<React.Fragment>
                        <Grid item xs={2}>
                            <Stack direction="column" gap={1}>
                                <Typography variant="caption">
                                    Outcome
                                </Typography>
                                <Box>
                                    <Select
                                        open={openOutcome}
                                        onClose={handleOutcomeClose}
                                        onOpen={handleOutcomeOpen}
                                        value={formik.values.outcome}
                                        onChange={handleOutcomeChange}
                                        size='small'
                                    >
                                        {outcomeList.map((outcome) => (
                                            <MenuItem key={outcome} value={outcome}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                    {outcome}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={10} />
                    </React.Fragment>)
                }
                <Grid item xs={12}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Name of driver
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Comment
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="driver_name"
                                value={formik.values.driver_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextFieldCustom
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default RevenueValueDriverMarketingForm                